import '../css/info.css';
import { appState } from './stateManager';
import { FLAG_ICON, getDateString, RECORDS_LINK_PREFIX, STEP_PX, TIMELINE_PADDING } from './info.details';
import { map } from './details';
import InfoSidebar from './info-sidebar';
import Legend from './legend';
import InfoCards from './info-cards';

export default class Info {
	constructor() {
		this.globalTimeline = document.getElementById('timeline');
		this.searchInput = document.getElementById('search-input-wrapper');
		this.filters = document.getElementById('filters');
		this.info = document.getElementById('info');
		this.closeBtn = document.getElementById('info-close-btn');
		this.timeline = document.getElementById('info-timeline');
		this.cityName = document.getElementById('info-city-name');
		this.dayCount = document.getElementById('info-day-count');
		this.victimsCount = document.getElementById('info-victims-count');
		this.recordsLink = document.getElementById('info-main-records-link');
		this.timelineMin = document.getElementById('info-timeline-label-min');
		this.timelineMax = document.getElementById('info-timeline-label-max');
		this.id = -1;
	}

	init() {
		appState.subscribe(this.onStateChange.bind(this));

		this.setupClose();

		this.cards = new InfoCards();
		this.cards.init();
		this.sidebar = new InfoSidebar();
		this.sidebar.init();
		this.legend = new Legend();
		this.legend.init();
	}

	setupClose() {
		if (!this.closeBtn || !this.info) return;
		this.closeBtn.onclick = () => {
			const currentState = appState.getState();
			appState.setState({ ...currentState, id: -1 });
		};

		this.info.onclick = (event) => {
			if (event.target.id !== this.info.id) return;
			const currentState = appState.getState();
			appState.setState({ ...currentState, id: -1 });
		};
	}

	onStateChange(state) {
		if (state.id === this.id) return;
		this.id = state.id;
		if (this.id === -1) {
			this.info?.classList.remove('active');
			this.legend?.hide();
			this.sidebar?.update();
			this.cards?.update();
			this.globalTimeline?.classList.remove('hide');
			this.searchInput?.classList.remove('hide');
			this.filters?.classList.remove('hide');
			return;
		}
		this.update(state.data, state.id);
	}

	update(data, id) {
		const relevantItems = this.getRelevantItems(data, id);
		const events = this.getEvents(relevantItems);
		this.updateMain(relevantItems);
		this.sidebar?.update(relevantItems);
		this.updateTimeline(events);
		this.cards?.update(events);
		this.info?.classList.add('active');
		this.legend?.show();
		this.globalTimeline?.classList.add('hide');
		this.searchInput?.classList.add('hide');
		this.filters?.classList.add('hide');
	}

	getRelevantItems(data, id) {
		const relevantItems = [];
		for (const item of data) {
			if (item.id === id) relevantItems.push(item);
		}
		return relevantItems;
	}

	getEvents(items) {
		const events = [...items];
		const bombEvents = [];

		for (const item of items) {
			const field = item['Hadműveleti légitámadás időpontja és az okozott veszteség/kár'];
			if (!field) continue;

			const parts = field
				.split(';')
				.map((s) => s.trim())
				.filter(Boolean);

			for (const part of parts) {
				const event = this.parseBombEvent(part);
				if (event) bombEvents.push(event);
			}
		}

		events.push(...bombEvents);
		events.sort((a, b) => this.getEventTime(a) - this.getEventTime(b));
		return events;
	}

	parseBombEvent(line) {
		const m = line.match(/^\s*(\d{4})\.(\d{1,2})\.(\d{1,2})\s+(.*)$/);
		if (!m) return null;

		const [, y, mth, d, text] = m;
		const date = new Date(Number(y), Number(mth) - 1, Number(d));

		return {
			date: date.getTime(),
			text: text.trim(),
			is_bomb: true,
			is_green: text.includes('nem') || text.includes('nincs')
		};
	}

	getEventTime(e) {
		// original items: use start_time if present
		if (e['A birtokváltás időpontja(i)']) {
			const t = new Date(e['A birtokváltás időpontja(i)']).getTime();
			if (!Number.isNaN(t)) return t;
		}
		// bomb events (or anything with date):
		if (e.date != null) {
			const t = typeof e.date === 'number' ? e.date : new Date(e.date).getTime();
			if (!Number.isNaN(t)) return t;
		}
		// fallback: shove unknown dates to the end
		return Infinity;
	}

	updateTimeline(items) {
		if (!this.timeline) return;
		this.timeline.innerHTML = '';
		const numEvents = items?.length || 0;
		if (!numEvents) return;

		const { minDate, maxDate } = this.getTimelineBounds(items);
		this.timelineMin.textContent = getDateString(new Date(minDate));
		this.timelineMax.textContent = getDateString(new Date(maxDate));

		const offsets = this.getTimelineOffsets(items);
		for (let t = 0; t < numEvents; t++) {
			const item = items[t];
			const dot = this.getTimelineDot(item, minDate, maxDate, t, offsets[t] || 0);
			if (dot) this.timeline.appendChild(dot);
		}
	}

	getTimelineBounds(items) {
		const dates = new Set();
		for (const item of items) {
			let itemDate = item['A birtokváltás időpontja(i)'] || null;
			if (itemDate == null) itemDate = item.date || null;
			if (itemDate == null) continue;
			const itemTimestamp = new Date(itemDate).getTime();
			dates.add(itemTimestamp);
		}
		let minDate = Math.min(...Array.from(dates));
		minDate -= TIMELINE_PADDING;
		let maxDate = Math.max(...Array.from(dates));
		maxDate += TIMELINE_PADDING;
		return { minDate, maxDate };
	}

	getTimelineDot(item, minDate, maxDate, index, xOffsetPx = 0) {
		let itemDate = item['A birtokváltás időpontja(i)'] || null;
		if (item.is_bomb) itemDate = item.date || null;
		if (itemDate == null) return null;

		const itemTimestamp = new Date(itemDate).getTime();
		const x = map(itemTimestamp, minDate, maxDate, 0, 1);

		const dot = document.createElement('div');
		dot.classList.add('info-timeline-dot');
		dot.dataset.index = index.toString() || '0';
		if (item.is_bomb) dot.classList.add('info-timeline-dot-bomb');
		if (item.is_bomb && item.is_green) dot.classList.add('info-timeline-dot-bomb-green');

		dot.style.setProperty('--x', `${x}`);
		dot.style.setProperty('--x-offset', `${xOffsetPx}px`);

		if (!item.is_bomb) dot.innerHTML = FLAG_ICON;
		dot.onclick = () => {
			this.onTimelineDotClick(index);
		};
		return dot;
	}

	onTimelineDotClick(index = 0) {
		this.cards?.toggleCards(index);
	}

	getTimelineOffsets(items) {
		const count = items?.length || 0;
		const offsets = new Array(count).fill(0);
		if (!count) return offsets;

		const byTime = new Map();
		for (let i = 0; i < count; i++) {
			const ts = this.getEventTime(items[i]);
			if (!Number.isFinite(ts)) continue;
			let group = byTime.get(ts);
			if (!group) {
				group = [];
				byTime.set(ts, group);
			}
			group.push(i);
		}

		for (const group of byTime.values()) {
			const n = group.length;
			if (n <= 1) continue;
			const center = (n - 1) / 2;
			for (let i = 0; i < n; i++) {
				const idx = group[i];
				offsets[idx] = (i - center) * STEP_PX;
			}
		}

		return offsets;
	}

	updateMain(items) {
		if (!this.cityName || !this.dayCount || !this.victimsCount || !this.recordsLink) return;
		this.cityName.textContent = 'N/A';
		this.dayCount.textContent = 'N/A';
		this.victimsCount.textContent = 'N/A';
		if (!items.length) return;
		this.cityName.textContent = items[0]['A település mai neve'] || 'N/A';
		this.dayCount.textContent = items[0]['A harctevékenység időtartama (naptári nap)'] || 'N/A';
		this.victimsCount.textContent = items[0].numRecords || 'N/A';

		const recordsLink = items[0].recordsLink || '';
		if (!recordsLink) {
			this.recordsLink.classList.add('hide');
			return;
		}
		this.recordsLink.href = RECORDS_LINK_PREFIX + recordsLink;
		this.recordsLink.classList.remove('hide');
	}
}
